// Message types - Client to Server
export const MSG_JOIN_GAME = 0x01;
export const MSG_READY = 0x02;
export const MSG_INPUT_STATE = 0x03;

// Message types - Server to Client
export const MSG_MATCH_FOUND = 0x10;
export const MSG_GAME_STATE = 0x11;
export const MSG_PIECE_LOCKED = 0x12;
export const MSG_EFFECT_APPLIED = 0x13;
export const MSG_GAME_OVER = 0x14;
export const MSG_COUNTDOWN = 0x15;
export const MSG_LOBBY_STATUS = 0x16;
export const MSG_READY_STATUS = 0x17;

export interface MatchInfo {
  playerId: number;
  opponentName: string;
  rngSeed: bigint;
}

export interface PlayerNetState {
  board: number[][];
  pieceType: number;
  pieceRotation: number;
  pieceX: number;
  pieceY: number;
  nextPiece: number;
  heldPiece: number;
  score: number;
  lines: number;
  level: number;
  gameOver: boolean;
  inventory: number[];
  inventoryCount: number;
  speedBoostTicks: number;
  flipScreenTicks: number;
}

export interface GameStateMsg {
  tick: bigint;
  players: [PlayerNetState, PlayerNetState];
}

export function encodeReady(): ArrayBuffer {
  const buf = new ArrayBuffer(1);
  const view = new DataView(buf);
  view.setUint8(0, MSG_READY);
  return buf;
}

export function encodeInputState(
  left: boolean,
  right: boolean,
  rotateCW: boolean,
  rotateCCW: boolean,
  softDrop: boolean,
  hardDrop: boolean,
  hold: boolean,
  powerupSlot: number
): ArrayBuffer {
  const buf = new ArrayBuffer(3);
  const view = new DataView(buf);

  let flags = 0;
  if (left) flags |= 0x01;
  if (right) flags |= 0x02;
  if (rotateCW) flags |= 0x04;
  if (rotateCCW) flags |= 0x08;
  if (softDrop) flags |= 0x10;
  if (hardDrop) flags |= 0x20;
  if (hold) flags |= 0x40;

  view.setUint8(0, MSG_INPUT_STATE);
  view.setUint8(1, flags);
  view.setUint8(2, powerupSlot);

  return buf;
}

const MATCH_FOUND_SIZE = 42; // 1 + 1 + 32 + 8

export function decodeMatchFound(data: ArrayBuffer): MatchInfo {
  if (data.byteLength < MATCH_FOUND_SIZE) {
    throw new Error(`Invalid match found packet: expected ${MATCH_FOUND_SIZE} bytes, got ${data.byteLength}`);
  }

  const view = new DataView(data);
  const decoder = new TextDecoder();

  const playerId = view.getUint8(1);
  const nameBytes = new Uint8Array(data, 2, 32);
  const nullIdx = nameBytes.indexOf(0);
  const opponentName = decoder.decode(nameBytes.subarray(0, nullIdx >= 0 ? nullIdx : 32));

  // Read 64-bit seed as little-endian
  let rngSeed = BigInt(0);
  for (let i = 0; i < 8; i++) {
    rngSeed |= BigInt(view.getUint8(34 + i)) << BigInt(i * 8);
  }

  return { playerId, opponentName, rngSeed };
}

const BOARD_WIDTH = 10;
const BOARD_HEIGHT = 20;
const PLAYER_STATE_SIZE = 233;

const GAME_STATE_SIZE = 1 + 8 + 2 * PLAYER_STATE_SIZE; // 475 bytes

export function decodeGameState(data: ArrayBuffer): GameStateMsg {
  if (data.byteLength < GAME_STATE_SIZE) {
    throw new Error(`Invalid game state packet: expected ${GAME_STATE_SIZE} bytes, got ${data.byteLength}`);
  }

  const view = new DataView(data);

  // Read tick (8 bytes little-endian)
  let tick = BigInt(0);
  for (let i = 0; i < 8; i++) {
    tick |= BigInt(view.getUint8(1 + i)) << BigInt(i * 8);
  }

  const player0 = decodePlayerState(data, 9);
  const player1 = decodePlayerState(data, 9 + PLAYER_STATE_SIZE);

  return { tick, players: [player0, player1] };
}

function decodePlayerState(data: ArrayBuffer, offset: number): PlayerNetState {
  if (data.byteLength < offset + PLAYER_STATE_SIZE) {
    throw new Error(`Buffer too small for player state at offset ${offset}: expected ${offset + PLAYER_STATE_SIZE} bytes, got ${data.byteLength}`);
  }

  const view = new DataView(data);
  const bytes = new Uint8Array(data);

  // Board (200 bytes)
  const board: number[][] = [];
  for (let y = 0; y < BOARD_HEIGHT; y++) {
    const row: number[] = [];
    for (let x = 0; x < BOARD_WIDTH; x++) {
      row.push(bytes[offset + y * BOARD_WIDTH + x]);
    }
    board.push(row);
  }

  // Current piece (4 bytes)
  const pieceType = bytes[offset + 200];
  const pieceRotation = bytes[offset + 201];
  const pieceX = bytes[offset + 202] - 128;
  const pieceY = bytes[offset + 203] - 128;

  // Next and held piece
  const nextPiece = bytes[offset + 204];
  const heldPiece = bytes[offset + 205];

  // Score (4 bytes little-endian)
  const score = view.getUint32(offset + 206, true);

  // Lines (4 bytes little-endian)
  const lines = view.getUint32(offset + 210, true);

  // Level and flags
  const level = bytes[offset + 214];
  const gameOver = bytes[offset + 215] !== 0;

  // Inventory (9 bytes)
  const inventory: number[] = [];
  for (let i = 0; i < 8; i++) {
    inventory.push(bytes[offset + 216 + i]);
  }
  const inventoryCount = bytes[offset + 224];

  // Effects
  const speedBoostTicks = view.getUint32(offset + 225, true);
  const flipScreenTicks = view.getUint32(offset + 229, true);

  return {
    board,
    pieceType,
    pieceRotation,
    pieceX,
    pieceY,
    nextPiece,
    heldPiece,
    score,
    lines,
    level,
    gameOver,
    inventory,
    inventoryCount,
    speedBoostTicks,
    flipScreenTicks,
  };
}

export function decodeCountdown(data: ArrayBuffer): number {
  if (data.byteLength < 5) {
    throw new Error(`Invalid countdown packet: expected 5 bytes, got ${data.byteLength}`);
  }
  const view = new DataView(data);
  return view.getUint32(1, true);
}

export function decodeGameOver(data: ArrayBuffer): number {
  if (data.byteLength < 2) {
    throw new Error(`Invalid game over packet: expected 2 bytes, got ${data.byteLength}`);
  }
  const view = new DataView(data);
  return view.getUint8(1);
}

export function decodeLobbyStatus(data: ArrayBuffer): number {
  if (data.byteLength < 2) {
    throw new Error(`Invalid lobby status packet: expected 2 bytes, got ${data.byteLength}`);
  }
  return new DataView(data).getUint8(1);
}

export interface ReadyStatus {
  player0Ready: boolean;
  player1Ready: boolean;
}

export function decodeReadyStatus(data: ArrayBuffer): ReadyStatus {
  if (data.byteLength < 3) {
    throw new Error(`Invalid ready status packet: expected 3 bytes, got ${data.byteLength}`);
  }
  const view = new DataView(data);
  return {
    player0Ready: view.getUint8(1) !== 0,
    player1Ready: view.getUint8(2) !== 0,
  };
}
